import {
  Fragment,
  FormEvent,
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import ReactMarkdown from "react-markdown";
import { cleanLegacyReading } from "./legacy-reading";
import { ReadingInline } from "./reading-inline";
import { AnalyticsDashboard } from "./analytics-dashboard";
import { pauseAnalyticsReading, setAnalyticsContext, startAnalytics, trackFeature } from "./analytics-client";
import {
  AiAnswerContent,
  type AnswerCitation,
} from "./ai-answer";
import { immersiveCommand, immersiveControlLabel } from "./immersive-reading";
import {
  isReadingDocument,
  type ReadingArticle,
  type ReadingDocument,
  type ReadingImage,
} from "./reading-document";
import {
  DEFAULT_READING_PREFERENCES,
  parseReadingPreferences,
  readingPreferenceStyle,
  type ReadingPreferences,
  type ReadingTheme,
} from "./reading-preferences";
import {
  DIRECT_READER_JUMP,
  TOC_REVEAL,
  chineseTocTitle,
  compactTocSection,
  isReaderDrawerView,
  tocAccessibleLabel,
  type ReaderView,
} from "./reader-layout";
import {
  newestIssueIds,
  publicationTheme,
  type PublicationThemeKey,
} from "./publication-theme";
import {
  formatPodcastTime,
  isArticleDocument,
  isTranscriptDocument,
  podcastSectionTitle,
  transcriptTurns,
  type ArticleDocument,
  type TranscriptChapter,
  type TranscriptDocument,
} from "./podcast-document";
import {
  formatPodcastDuration,
  formatPodcastPublishedAt,
} from "./podcast-card-meta";
import { PodcastPlayer } from "./podcast-player";
import {
  HomeSections,
  type ContinueReadingItem,
  type HomeArticle,
  type HomeIssue,
  type HomePayload,
  type HomePodcast,
} from "./home-sections";
import {
  LibraryNavigation,
  librarySectionFromSearch,
  type LibrarySection,
} from "./library-navigation";
import { AboutPage } from "./about-page";
import {
  ENTRY_QUOTE_STORAGE_KEY,
  selectEntryQuote,
} from "./entry-quotes";
import { periodicalDeepLink, podcastDeepLink } from "./deep-link";
import { issueReaderUrl, readingArticleFromLocation, libraryLocationFromUrl } from "./reader-navigation";
import { readingResumeArticleId, articleReadingProgress, type ReadingProgress } from "./reading-resume";
import { podcastCanonicalPath } from "../podcast-links";
import { safeAuthReturnTo } from "../auth-return";
import "./styles.css";

type User = {
  id: string;
  name: string;
  avatar_url: string | null;
  role: "reader" | "editor" | "admin";
  canViewAnalytics?: boolean;
};

type Issue = {
  id: string;
  title: string;
  publication: string;
  issue_label: string;
  issue_date: string | null;
  language: string;
  summary: string;
  page_count: number;
  file_size: number;
  progress_page: number | null;
  progress_ratio: number | null;
  pdfUrl?: string;
  coverUrl?: string | null;
  textUrl?: string | null;
  readingUrl?: string | null;
  reading_status?: string;
  article_count?: number | null;
};

type PodcastEpisode = {
  id: string;
  publicSlug: string;
  feed_id: string;
  feed_title: string;
  category: string;
  title: string;
  description: string;
  published_at: string | null;
  duration_seconds: number | null;
  source_url: string;
  original_audio_url: string;
  artwork_url: string | null;
  summary: string;
  tags: string[];
  topics: string[];
  transcript_characters: number;
  dialogue_duration_seconds: number | null;
  model: string | null;
  progress_position: number | null;
  progress_ratio: number | null;
  transcriptUrl: string | null;
  articleUrl: string | null;
  dialogueUrl: string | null;
  audioUrl: string | null;
  sourceAudioUrl: string | null;
  transcriptReady: boolean;
};

type PodcastFacet = {
  id: string;
  label: string;
  total: number;
};

type PodcastFacets = {
  feeds: PodcastFacet[];
  topics: PodcastFacet[];
};

type PodcastLibraryPayload = {
  episodes: PodcastEpisode[];
  total: number;
  nextOffset: number | null;
  facets: PodcastFacets;
};

type Comment = {
  id: string;
  article_id: string | null;
  article_title: string | null;
  page_number: number | null;
  body: string;
  created_at: string;
  user_name: string;
  user_avatar: string | null;
};

type ArticleContext = {
  id: string;
  title: string;
  pageStart: number;
  pageEnd: number;
};

type TopicResult = {
  content_type: "article";
  article_id: string;
  global_id: string;
  issue_id: string;
  title: string;
  section: string;
  summary: string;
  byline: string | null;
  tags: string[];
  page_start: number;
  page_end: number;
  image_count: number;
  publication: string;
  issue_title: string;
  issue_label: string;
  issue_date: string | null;
  match_type: "hybrid" | "keyword" | "semantic";
  keyword_rank: number | null;
  semantic_rank: number | null;
  similarity: number | null;
  relevance_score: number;
  match_reason: string;
};

type PodcastTopicResult = PodcastEpisode & {
  content_type: "podcast";
  match_type: "keyword";
  relevance_score: number;
  match_reason: string;
};

type TopicSearchResult = TopicResult | PodcastTopicResult;
type TopicSearchScope = "all" | "article" | "podcast";

type ApiEnvelope<T> = T & {
  ok: boolean;
  error?: { message: string };
};

type View = ReaderView;
type PublicationFilter = PublicationThemeKey | "all";
type EngagementEventType =
  | "home_view"
  | "issue_open"
  | "article_open"
  | "engaged_read"
  | "read_complete"
  | "audio_start"
  | "audio_25"
  | "audio_50"
  | "audio_75"
  | "audio_complete";
type EngagementContentType = "home" | "issue" | "article" | "podcast";
type EngagementSource = "home" | "library" | "search" | "reader" | "player";

type EngagementEvent = {
  eventType: EngagementEventType;
  contentType: EngagementContentType;
  contentId: string;
  issueId?: string | null;
  progress?: number | null;
  durationSeconds?: number | null;
  source: EngagementSource;
};

type PublicationStyle = CSSProperties & {
  "--publication-accent": string;
  "--publication-surface": string;
  "--publication-on-accent": string;
};

async function api<T>(url: string, init?: RequestInit): Promise<ApiEnvelope<T>> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const payload = (await response.json()) as ApiEnvelope<T>;
  if (!response.ok) {
    throw new Error(payload.error?.message ?? "请求失败");
  }
  return payload;
}

function IssueCover({
  issue,
  isNewest,
}: {
  issue: Issue;
  isNewest: boolean;
}): React.JSX.Element {
  const [imageFailed, setImageFailed] = useState(false);
  const theme = publicationTheme(issue.publication);
  const style: PublicationStyle = {
    "--publication-accent": theme.accent,
    "--publication-surface": theme.surface,
    "--publication-on-accent": theme.inkOnAccent,
  };
  return (
    <div className={`cover-wrap publication-${theme.key}`} style={style}>
      {issue.coverUrl && !imageFailed ? (
        <img
          src={issue.coverUrl}
          alt={`${issue.title}封面`}
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className="cover-fallback">
          <div className="fallback-masthead">
            <span>{theme.shortLabel}</span>
            <small>{issue.issue_label}</small>
          </div>
          <b>{issue.title.replace(`${issue.publication} · `, "")}</b>
          <em>FOLIO EDITION</em>
        </div>
      )}
      {isNewest && <span className="new-ribbon">NEW</span>}
    </div>
  );
}

function imageBuckets(article: ReadingArticle): Map<number, ReadingImage[]> {
  const buckets = new Map<number, ReadingImage[]>();
  const images = article.images ?? [];
  if (article.blocks.length === 0) return buckets;
  const pageSpan = Math.max(1, article.pageEnd - article.pageStart + 1);
  for (const image of images.slice(1)) {
    const pagePosition = (image.page - article.pageStart + 0.5) / pageSpan;
    const blockIndex = Math.max(
      0,
      Math.min(
        article.blocks.length - 1,
        Math.floor(pagePosition * article.blocks.length),
      ),
    );
    const bucket = buckets.get(blockIndex) ?? [];
    bucket.push(image);
    buckets.set(blockIndex, bucket);
  }
  return buckets;
}

function ArticleFigures({
  images,
  version,
  hero = false,
}: {
  images: ReadingImage[];
  version: string;
  hero?: boolean;
}): React.JSX.Element | null {
  if (images.length === 0) return null;
  return (
    <div className={`article-figures${hero ? " hero" : ""}${images.length > 1 ? " multiple" : ""}`}>
      {images.map((image) => (
        <figure key={image.id}>
          <div className="article-image-link">
            <img
              src={`${image.src}${image.src.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`}
              alt={image.caption || `原刊第 ${image.page} 页配图`}
              width={image.width}
              height={image.height}
              loading="lazy"
              decoding="async"
            />
          </div>
          <figcaption><ReadingInline text={image.caption || `原刊配图 · P.${image.page}`} /></figcaption>
        </figure>
      ))}
    </div>
  );
}

function Login(): React.JSX.Element {
  const params = new URLSearchParams(window.location.search);
  const loginParams = new URLSearchParams({
    return_to: safeAuthReturnTo(
      `${window.location.pathname}${window.location.search}`,
    ),
  });
  return (
    <main className="login-shell">
      <section className="login-card">
        <p className="eyebrow">CURATED LONGFORM</p>
        <img className="masthead-mark" src="/brand-mark.svg" alt="" />
        <h1>FOLIO</h1>
        <p className="login-lead">回到原文，形成判断。</p>
        <div className="login-rule" />
        <p className="login-copy">
          期刊、长播客、主题检索与 AI 辅助研读，仅向已授权的飞书成员开放。
        </p>
        {params.get("auth") === "denied" && (
          <p className="error-banner">飞书授权未完成，请重新登录。</p>
        )}
        <a
          className="primary-button login-button"
          href={`/auth/login?${loginParams.toString()}`}
        >
          使用飞书登录
          <span aria-hidden="true">→</span>
        </a>
        <p className="privacy-note">使用公司飞书身份进行访问控制，登录、阅读与功能使用记录用于内部服务优化，仅开发者可查看。</p>
      </section>
    </main>
  );
}

function BootScreen(): React.JSX.Element {
  const [quote] = useState(() => {
    let previous: string | null = null;
    try {
      previous = window.sessionStorage.getItem(ENTRY_QUOTE_STORAGE_KEY);
    } catch {
      previous = null;
    }

    const next = selectEntryQuote(previous);
    try {
      window.sessionStorage.setItem(ENTRY_QUOTE_STORAGE_KEY, next);
    } catch {
      // The entrance remains useful when browser storage is unavailable.
    }
    return next;
  });

  return (
    <main className="boot-screen" aria-live="polite">
      <section className="boot-message">
        <p className="boot-kicker">FOLIO · 回到原文，形成判断</p>
        <blockquote>{quote}</blockquote>
        <span className="boot-rule" aria-hidden="true" />
        <p className="boot-status">正在整理长内容</p>
      </section>
    </main>
  );
}

function TextReader({
  issue,
  targetArticleId,
  initialPage,
  jumpRequest,
  onReadingProgress,
  immersive,
  onToggleImmersive,
  onActiveArticle,
  onDiscuss,
  onResearch,
  onArticleEngagement,
}: {
  issue: Issue;
  targetArticleId: string | null;
  initialPage: number;
  jumpRequest: number;
  onReadingProgress: (progress: ReadingProgress) => void;
  immersive: boolean;
  onToggleImmersive: () => void;
  onActiveArticle: (article: ArticleContext | null) => void;
  onDiscuss: (article: ArticleContext) => void;
  onResearch: (article: ArticleContext) => void;
  onArticleEngagement: (
    eventType: "article_open" | "engaged_read" | "read_complete",
    article: ReadingArticle,
    progress: number,
    durationSeconds: number,
  ) => void;
}): React.JSX.Element {
  const [reading, setReading] = useState<ReadingDocument | null>(null);
  const [legacyText, setLegacyText] = useState("");
  const [activeArticle, setActiveArticle] = useState("");
  const [readingLanguage, setReadingLanguage] = useState<"zh" | "en">("zh");
  const [readingRetry, setReadingRetry] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [preferences, setPreferences] = useState<ReadingPreferences>(() => {
    if (typeof window === "undefined") return DEFAULT_READING_PREFERENCES;
    return parseReadingPreferences(window.localStorage.getItem("hiic-reading-preferences"));
  });
  const [error, setError] = useState("");
  const articleNodes = useRef<Record<string, HTMLElement | null>>({});
  const tocNodes = useRef<Record<string, HTMLButtonElement | null>>({});
  const engagedArticles = useRef(new Set<string>());
  const completedArticles = useRef(new Set<string>());
  const engagementCallback = useRef(onArticleEngagement);
  engagementCallback.current = onArticleEngagement;

  useEffect(() => {
    const controller = new AbortController();
    setReading(null);
    setLegacyText("");
    setActiveArticle("");
    setReadingLanguage("zh");
    setSettingsOpen(false);
    setTocOpen(false);
    setError("");
    onActiveArticle(null);
    engagedArticles.current.clear();
    completedArticles.current.clear();
    const readingUrl = issue.readingUrl;
    if (readingUrl) {
      void fetch(readingUrl, { credentials: "same-origin", signal: controller.signal })
        .then(async (response) => {
          if (!response.ok) throw new Error("文字阅读版尚未生成");
          const payload: unknown = await response.json();
          if (!isReadingDocument(payload)) throw new Error("文字阅读版格式无效");
          setReading(payload);
          setActiveArticle(targetArticleId ?? "issue-overview");
        })
        .catch((reason: unknown) => {
          if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "读取失败");
        });
      return () => controller.abort();
    }
    void fetch(issue.textUrl ?? `/api/issues/${encodeURIComponent(issue.id)}/text`, {
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("全文尚未生成");
        return response.text();
      })
      .then((text) => {
        if (!cleanLegacyReading(text).trim()) throw new Error("本期正文尚未准备好，请稍后重试");
        setLegacyText(text);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "读取失败");
      });
    return () => controller.abort();
  }, [
    issue.id,
    issue.readingUrl,
    issue.textUrl,
    onActiveArticle,
    targetArticleId,
    readingRetry,
  ]);

  useEffect(() => {
    if (!reading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top);
        const id = visible[0]?.target.id;
        if (id) setActiveArticle(id);
      },
      { rootMargin: "-18% 0px -70% 0px", threshold: 0 },
    );
    for (const article of [
      { id: "issue-overview" },
      ...reading.articles,
    ]) {
      const node = articleNodes.current[article.id];
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [reading]);

  useEffect(() => {
    if (!reading) return;
    const resumeId = readingResumeArticleId(reading.articles, targetArticleId, initialPage);
    if (resumeId === "issue-overview") return;
    const frame = window.requestAnimationFrame(() => {
      const target = articleNodes.current[resumeId];
      if (!target) return;
      setActiveArticle(resumeId);
      target.scrollIntoView(DIRECT_READER_JUMP);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [reading, targetArticleId, initialPage, jumpRequest]);

  useEffect(() => {
    const article = reading?.articles.find((item) => item.id === activeArticle);
    const node = articleNodes.current[activeArticle];
    if (!article || !node) return;
    let previousPage = -1;
    function savePosition(): void {
      if (document.visibilityState !== "visible" || !node || !article) return;
      const bounds = node.getBoundingClientRect();
      const progress = articleReadingProgress(article, (160 - bounds.top) / Math.max(1, bounds.height), issue.page_count);
      if (progress.pageNumber === previousPage) return;
      previousPage = progress.pageNumber;
      onReadingProgress(progress);
    }
    const interval = window.setInterval(savePosition, 1500);
    return () => window.clearInterval(interval);
  }, [activeArticle, reading, issue.page_count, onReadingProgress]);

  useEffect(() => {
    if (!reading || !activeArticle || activeArticle === "issue-overview") return;
    const article = reading.articles.find((item) => item.id === activeArticle);
    const node = articleNodes.current[activeArticle];
    if (!article || !node) return;

    engagementCallback.current("article_open", article, 0, 0);
    let visibleSeconds = 0;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      visibleSeconds += 1;
      const bounds = node.getBoundingClientRect();
      const progress = Math.max(
        0,
        Math.min(1, (window.innerHeight - bounds.top) / Math.max(1, bounds.height)),
      );
      if (
        visibleSeconds >= 30
        && progress >= 0.25
        && !engagedArticles.current.has(article.id)
      ) {
        engagedArticles.current.add(article.id);
        engagementCallback.current("engaged_read", article, progress, visibleSeconds);
      }
      if (
        progress >= 0.8
        && engagedArticles.current.has(article.id)
        && !completedArticles.current.has(article.id)
      ) {
        completedArticles.current.add(article.id);
        engagementCallback.current("read_complete", article, progress, visibleSeconds);
      }
    }, 1_000);
    return () => window.clearInterval(interval);
  }, [activeArticle, reading]);

  useEffect(() => {
    if (!reading || !activeArticle || activeArticle === "issue-overview") {
      onActiveArticle(null);
      return;
    }
    const article = reading.articles.find((item) => item.id === activeArticle);
    onActiveArticle(article
      ? {
          id: article.id,
          title: article.title,
          pageStart: article.pageStart,
          pageEnd: article.pageEnd,
        }
      : null);
  }, [activeArticle, onActiveArticle, reading]);

  useEffect(() => {
    if (!activeArticle || activeArticle === "issue-overview") return;
    tocNodes.current[activeArticle]?.scrollIntoView(TOC_REVEAL);
  }, [activeArticle]);

  useEffect(() => {
    window.localStorage.setItem(
      "hiic-reading-preferences",
      JSON.stringify(preferences),
    );
  }, [preferences]);

  useLayoutEffect(() => {
    if (!tocOpen) return;
    const mobile = window.matchMedia("(max-width: 1180px)");
    let unlock: (() => void) | undefined;
    const syncLock = (): void => {
      unlock?.();
      unlock = undefined;
      if (!mobile.matches) return;
      const body = document.body;
      const root = document.documentElement;
      const room = document.querySelector<HTMLElement>(".reading-room");
      const x = window.scrollX;
      const y = window.scrollY;
      const roomScroll = room?.scrollTop ?? 0;
      const previous = {
        position: body.style.position, top: body.style.top,
        left: body.style.left, width: body.style.width,
        overflow: root.style.overflow, roomOverflow: room?.style.overflow ?? "",
      };
      body.classList.add("reader-toc-open");
      body.style.position = "fixed";
      body.style.top = `-${y}px`;
      body.style.left = `-${x}px`;
      body.style.width = "100%";
      root.style.overflow = "hidden";
      if (room && immersive) room.style.overflow = "hidden";
      unlock = () => {
        body.classList.remove("reader-toc-open");
        body.style.position = previous.position;
        body.style.top = previous.top;
        body.style.left = previous.left;
        body.style.width = previous.width;
        root.style.overflow = previous.overflow;
        if (room) {
          room.style.overflow = previous.roomOverflow;
          room.scrollTop = roomScroll;
        }
        window.scrollTo({ left: x, top: y, behavior: "instant" });
      };
    };
    syncLock();
    mobile.addEventListener("change", syncLock);
    return () => {
      mobile.removeEventListener("change", syncLock);
      unlock?.();
    };
  }, [tocOpen, immersive]);

  function updateTheme(theme: ReadingTheme): void {
    setPreferences((current) => ({ ...current, theme }));
  }

  function jumpTo(articleId: string): void {
    const target = articleNodes.current[articleId];
    if (!target) return;
    setActiveArticle(articleId);
    // The drawer's scroll lock must be released before moving the manuscript.
    if (tocOpen) window.requestAnimationFrame(() => target.scrollIntoView(DIRECT_READER_JUMP));
    else target.scrollIntoView(DIRECT_READER_JUMP);
    window.history.replaceState(window.history.state, "", issueReaderUrl(issue.id, articleId, window.location.search));
    setTocOpen(false);
  }

  if (error) return (
    <section className="reader-panel empty-state" role="alert">
      <h2>文字版暂时无法打开</h2><p>{error}</p>
      <button className="primary-button" onClick={() => setReadingRetry((value) => value + 1)}>重新加载</button>
    </section>
  );
  if (!reading && legacyText) {
    return (
      <article className="reader-panel text-reader">
        <div className="legacy-reading-sheet">
          <header className="legacy-reading-header">
            <p className="eyebrow">{issue.publication} · {issue.issue_label}</p>
            <h1>{issue.language === "en" ? "原文预览" : "正文预览"}</h1>
            <p role="status">{issue.language === "en"
              ? "本期中文翻译与分篇阅读版尚未完成，以下为原文预览。"
              : "本期分篇阅读版尚未完成，以下为正文预览。"}</p>
          </header>
          <div className="legacy-reading-copy" lang={issue.language === "en" ? "en" : "zh-CN"}>
            <ReactMarkdown skipHtml components={{ img: () => null }}>{cleanLegacyReading(legacyText)}</ReactMarkdown>
          </div>
        </div>
      </article>
    );
  }
  if (!reading) return <section className="reader-panel"><div className="loader">正在编排文字版…</div></section>;
  const bilingual = reading.articles.some((article) => article.source !== undefined);
  const overview = readingLanguage === "en" && reading.sourceOverview
    ? reading.sourceOverview
    : reading.overview;
  return (
    <section
      className={`reader-panel reading-edition reading-theme-${preferences.theme}`}
      style={readingPreferenceStyle(preferences)}
      onKeyDown={(event) => { if (event.key === "Escape") { setTocOpen(false); setSettingsOpen(false); } }}
    >
      <div className="reading-tools" aria-label="阅读设置">
        <button
          className="toc-drawer-trigger" data-analytics-feature="toc_open"
          aria-expanded={tocOpen}
          aria-controls="article-toc"
          onClick={() => setTocOpen(true)}
        >
          目录
        </button>
        {bilingual && (
          <div className="language-switch" role="group" aria-label="正文语言">
            <button
              className={readingLanguage === "zh" ? "active" : ""}
              aria-pressed={readingLanguage === "zh"}
              data-analytics-feature="language_zh" onClick={() => setReadingLanguage("zh")}
            >
              CN
            </button>
            <button
              className={readingLanguage === "en" ? "active" : ""}
              aria-pressed={readingLanguage === "en"}
              data-analytics-feature="language_en" onClick={() => setReadingLanguage("en")}
            >
              EN
            </button>
          </div>
        )}
        <button
          className="immersive-reading-trigger" data-analytics-feature="immersive_toggle"
          aria-label={immersiveControlLabel(immersive)}
          aria-pressed={immersive}
          title={immersiveControlLabel(immersive)}
          onClick={onToggleImmersive}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d={immersive
              ? "M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"
              : "M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5"} />
          </svg>
        </button>
        <button
          className="reading-settings-trigger" data-analytics-feature="reading_settings"
          aria-label="阅读设置"
          aria-expanded={settingsOpen}
          aria-controls="reading-settings-panel"
          onClick={() => setSettingsOpen((open) => !open)}
        >
          <span>Aa</span>
          <i className={`theme-dot theme-dot-${preferences.theme}`} aria-hidden="true" />
        </button>
        {settingsOpen && (
          <section className="reading-settings-panel" id="reading-settings-panel">
            <header>
              <span>阅读设置</span>
              <button aria-label="关闭阅读设置" onClick={() => setSettingsOpen(false)}>×</button>
            </header>
            <div className="theme-options" role="group" aria-label="阅读背景">
              {([
                ["paper", "纸张"],
                ["sage", "护眼"],
                ["night", "夜读"],
              ] as Array<[ReadingTheme, string]>).map(([theme, label]) => (
                <button
                  className={preferences.theme === theme ? "active" : ""}
                  key={theme}
                  data-analytics-feature="theme_change" onClick={() => updateTheme(theme)}
                  aria-pressed={preferences.theme === theme}
                >
                  <i className={`theme-swatch theme-swatch-${theme}`} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
            <div className="text-scale-row">
              <span>字号</span>
              <div role="group" aria-label="正文字号">
                {([
                  [0.9, "小"],
                  [1, "中"],
                  [1.15, "大"],
                ] as Array<[number, string]>).map(([textScale, label]) => (
                  <button
                    className={preferences.textScale === textScale ? "active" : ""}
                    key={textScale}
                    data-analytics-feature="font_change"
                    onClick={() => setPreferences((current) => ({
                      ...current,
                      textScale,
                    }))}
                    aria-pressed={preferences.textScale === textScale}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button
              className="focus-reading-action" data-analytics-feature="immersive_toggle"
              aria-pressed={immersive}
              onClick={() => {
                onToggleImmersive();
                setSettingsOpen(false);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={immersive
                  ? "M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"
                  : "M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5"} />
              </svg>
              <span>
                <b>{immersive ? "退出专注" : "专注阅读"}</b>
                <small>{immersive ? "恢复期刊信息栏" : "隐藏期刊信息栏与浏览器干扰"}</small>
              </span>
            </button>
          </section>
        )}
      </div>
      {tocOpen && (
        <button
          className="toc-scrim"
          aria-label="关闭本期目录"
          onClick={() => setTocOpen(false)}
        />
      )}
      <aside
        className={`article-toc${tocOpen ? " is-open" : ""}`}
        id="article-toc"
        aria-label="本期目录"
      >
        <header>
          <div>
            <p className="eyebrow">CONTENTS</p>
            <h2>本期目录</h2>
            <span>{reading.articles.length} 篇文章</span>
          </div>
          <button
            className="toc-close"
            aria-label="关闭本期目录"
            onClick={() => setTocOpen(false)}
          >
            ×
          </button>
        </header>
        <nav>
          <button
            className={`toc-overview${activeArticle === "issue-overview" ? " active" : ""}`}
            data-analytics-feature="toc_select" onClick={() => jumpTo("issue-overview")}
          >
            <span>00 · 导读</span>
            <b>本期内容地图</b>
            <small>TOP</small>
          </button>
          {reading.articles.map((article) => (
            (() => {
              const source = readingLanguage === "en" ? article.source : undefined;
              const section = source?.section ?? article.section;
              const title = source?.title ?? chineseTocTitle(article.title);
              const isActive = activeArticle === article.id;
              return (
                <button
                  aria-current={isActive ? "location" : undefined}
                  aria-label={tocAccessibleLabel(section, title, article.pageStart)}
                  className={isActive ? "active" : ""}
                  key={article.id}
                  data-analytics-feature="toc_select" onClick={() => jumpTo(article.id)}
                  ref={(node) => {
                    tocNodes.current[article.id] = node;
                  }}
                  title={title}
                >
                  <span>{compactTocSection(section)}</span>
                  <b>{title}</b>
                  <small>P.{article.pageStart}</small>
                </button>
              );
            })()
          ))}
        </nav>
      </aside>
      <article className="reading-manuscript" lang={readingLanguage === "en" ? "en" : "zh-CN"}>
        <header
          className="issue-overview"
          id="issue-overview"
          ref={(node) => {
            articleNodes.current["issue-overview"] = node;
          }}
        >
          <p className="eyebrow">EDITOR&apos;S MAP</p>
          <h2>本期导读</h2>
          <p>{overview}</p>
        </header>
        {reading.articles.map((article, index) => {
          const images = article.images ?? [];
          const source = readingLanguage === "en" ? article.source : undefined;
          const presentedArticle: ReadingArticle = source
            ? {
                ...article,
                section: source.section,
                title: source.title,
                summary: source.summary,
                blocks: source.blocks,
              }
            : article;
          const buckets = imageBuckets(presentedArticle);
          return (
            <section
              className="reading-article"
              id={article.id}
              key={article.id}
              ref={(node) => {
                articleNodes.current[article.id] = node;
              }}
            >
              <header>
                <div className="article-kicker">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <p>{presentedArticle.section}</p>
                </div>
                <h2><ReadingInline text={presentedArticle.title} /></h2>
                <p className="article-summary"><ReadingInline text={presentedArticle.summary} /></p>
                <div className="article-meta">
                  {article.byline && <span>文｜{article.byline}</span>}
                  <div className="article-actions">
                    <button data-analytics-feature="discussion_open" onClick={() => onDiscuss({
                      id: article.id,
                      title: article.title,
                      pageStart: article.pageStart,
                      pageEnd: article.pageEnd,
                    })}>
                      讨论这篇
                    </button>
                    <button data-analytics-feature="ai_open" onClick={() => onResearch({
                      id: article.id,
                      title: article.title,
                      pageStart: article.pageStart,
                      pageEnd: article.pageEnd,
                    })}>
                      AI 研读
                    </button>
                  </div>
                </div>
                {readingLanguage === "zh" && article.tags.length > 0 && (
                  <div className="article-tags">
                    {article.tags.map((tag) => <span key={tag}>{tag}</span>)}
                  </div>
                )}
              </header>
              <ArticleFigures
                images={images.slice(0, 1)}
                version={reading.generatedAt}
                hero
              />
              <div className="article-copy">
                {presentedArticle.blocks.map((block, blockIndex) => {
                  const key = `${article.id}-${blockIndex}`;
                  const figures = buckets.get(blockIndex) ?? [];
                  return (
                    <Fragment key={key}>
                      {block.type === "subheading" ? (
                        <h3><ReadingInline text={block.text} /></h3>
                      ) : block.type === "caption" ? (
                        <p className="article-caption"><ReadingInline text={block.text} /></p>
                      ) : block.type === "table" ? (
                        <figure className="article-table-wrap">
                          {block.caption && <figcaption><ReadingInline text={block.caption} /></figcaption>}
                          <table className="article-table">
                            <thead>
                              <tr>
                                {block.rows[0].map((cell, cellIndex) => (
                                  <th
                                    key={`${key}-head-${cellIndex}`}
                                    scope="col"
                                    rowSpan={cell.rowSpan}
                                    colSpan={cell.colSpan}
                                  >
                                    <ReadingInline text={cell.text} />
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {block.rows.slice(1).map((row, rowIndex) => (
                                <tr key={`${key}-row-${rowIndex}`}>
                                  {row.map((cell, cellIndex) => (
                                    <td
                                      key={`${key}-cell-${rowIndex}-${cellIndex}`}
                                      rowSpan={cell.rowSpan}
                                      colSpan={cell.colSpan}
                                    >
                                      <ReadingInline text={cell.text} />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </figure>
                      ) : (
                        <p><ReadingInline text={block.text} /></p>
                      )}
                      <ArticleFigures images={figures} version={reading.generatedAt} />
                    </Fragment>
                  );
                })}
              </div>
            </section>
          );
        })}
      </article>
    </section>
  );
}

function Comments({
  issue,
  page,
  article,
}: {
  issue: Issue;
  page: number;
  article: ArticleContext | null;
}): React.JSX.Element {
  const [comments, setComments] = useState<Comment[]>([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [scope, setScope] = useState<"article" | "issue">(
    article ? "article" : "issue",
  );
  const scopedArticle = scope === "article" ? article : null;

  const load = useCallback(() => {
    const query = scopedArticle
      ? `?article=${encodeURIComponent(scopedArticle.id)}`
      : "";
    void api<{ comments: Comment[] }>(
      `/api/issues/${encodeURIComponent(issue.id)}/comments${query}`,
    )
      .then((payload) => setComments(payload.comments))
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "读取失败"));
  }, [issue.id, scopedArticle]);
  useEffect(load, [load]);

  useEffect(() => {
    setScope(article ? "article" : "issue");
  }, [article]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    trackFeature("comment_submit");
    setError("");
    try {
      await api(`/api/issues/${encodeURIComponent(issue.id)}/comments`, {
        method: "POST",
        body: JSON.stringify({
          body,
          pageNumber: scopedArticle?.pageStart ?? page,
          articleId: scopedArticle?.id ?? null,
        }),
      });
      setBody("");
      load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "评论失败");
    }
  }

  return (
    <section className="reader-panel discussion">
      <header>
        <p className="eyebrow">MARGINALIA</p>
        <h2>{scopedArticle ? `讨论 · ${scopedArticle.title}` : "本期讨论"}</h2>
        <p>
          {scopedArticle
            ? `评论将锚定在这篇文章，并保留原刊 P.${scopedArticle.pageStart}–${scopedArticle.pageEnd}。`
            : `评论将锚定在当前原刊第 ${page} 页。`}
        </p>
        {article && (
          <div className="discussion-scope" role="group" aria-label="评论范围">
            <button
              className={scope === "article" ? "active" : ""}
              onClick={() => setScope("article")}
            >
              当前文章
            </button>
            <button
              className={scope === "issue" ? "active" : ""}
              onClick={() => setScope("issue")}
            >
              本期全部
            </button>
          </div>
        )}
      </header>
      <form className="comment-form" onSubmit={(event) => void submit(event)}>
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="写下观点、疑问或值得分享的线索…"
          maxLength={2000}
          required
        />
        <button className="primary-button" type="submit">
          {scopedArticle ? "发表文章评论" : `发表到第 ${page} 页`}
        </button>
      </form>
      {error && <p className="error-banner">{error}</p>}
      <div className="comment-list">
        {comments.length === 0 && <p className="muted">还没有评论，留下第一条页边笔记。</p>}
        {comments.map((comment) => (
          <article className="comment" key={comment.id}>
            <div className="comment-avatar">
              {comment.user_avatar ? <img src={comment.user_avatar} alt="" /> : comment.user_name.slice(0, 1)}
            </div>
            <div>
              <div className="comment-meta">
                <b>{comment.user_name}</b>
                <span>
                  {comment.article_title
                    ? comment.article_title
                    : `第 ${comment.page_number ?? "—"} 页`}
                </span>
                <time>{new Date(comment.created_at).toLocaleDateString("zh-CN")}</time>
              </div>
              <p>{comment.body}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function AiAssistant({
  issue,
  article,
  onJump,
}: {
  issue: Issue;
  article: ArticleContext | null;
  onJump: (page: number) => void;
}): React.JSX.Element {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [citations, setCitations] = useState<AnswerCitation[]>([]);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState("");
  const [scope, setScope] = useState<"article" | "issue">(
    article ? "article" : "issue",
  );
  const scopedArticle = scope === "article" ? article : null;

  useEffect(() => {
    setScope(article ? "article" : "issue");
    setQuestion("");
    setAnswer("");
    setCitations([]);
  }, [article, issue.id]);

  async function ask(event: FormEvent): Promise<void> {
    event.preventDefault();
    trackFeature("ai_submit");
    setWaiting(true);
    setError("");
    setAnswer("");
    try {
      const payload = await api<{
        answer: string;
        citations: AnswerCitation[];
        provider: "deepseek";
      }>("/api/ask", {
        method: "POST",
        body: JSON.stringify({
          issueId: issue.id,
          articleId: scopedArticle?.id ?? null,
          question,
        }),
      });
      setAnswer(payload.answer);
      setCitations(payload.citations);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "提问失败");
    } finally {
      setWaiting(false);
    }
  }

  return (
    <section className="reader-panel ai-room">
      <header>
        <p className="eyebrow">READ WITH AI</p>
        <h2>{scopedArticle ? `研读 · ${scopedArticle.title}` : "向本期提问"}</h2>
        {article && (
          <div className="research-scope" role="group" aria-label="AI 研读范围">
            <button
              className={scope === "article" ? "active" : ""}
              onClick={() => {
                setScope("article");
                setAnswer("");
              }}
            >
              当前文章
            </button>
            <button
              className={scope === "issue" ? "active" : ""}
              onClick={() => {
                setScope("issue");
                setAnswer("");
              }}
            >
              整期期刊
            </button>
          </div>
        )}
      </header>
      <form className="ask-form" onSubmit={(event) => void ask(event)}>
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={scopedArticle
            ? "例如：这篇文章最重要的判断和证据是什么？"
            : "例如：本期对中国经济复苏有哪些判断？"}
          minLength={2}
          maxLength={500}
          required
        />
        <button className="primary-button" disabled={waiting} type="submit">
          {waiting ? "正在研读…" : "提问"}
        </button>
      </form>
      {error && <p className="error-banner">{error}</p>}
      {answer && (
        <article className="ai-answer">
          <header>
            <span>AI 研读</span>
          </header>
          <AiAnswerContent answer={answer} citations={citations} onJump={onJump} />
        </article>
      )}
      {!answer && !waiting && (
        <div className="prompt-grid">
          {(scopedArticle
            ? ["概括这篇文章的三个核心判断", "梳理论证结构和关键证据", "有哪些值得继续追问的问题？"]
            : ["概括本期最重要的三个观点", "有哪些值得管理层关注的趋势？", "列出关键数据和出处"]
          ).map((prompt) => (
            <button key={prompt} onClick={() => setQuestion(prompt)}>{prompt}</button>
          ))}
        </div>
      )}
    </section>
  );
}

function TopicSearch({
  open,
  opening,
  openError,
  onClose,
  onOpen,
  onOpenPodcast,
}: {
  open: boolean;
  opening: boolean;
  openError: string;
  onClose: () => void;
  onOpen: (issueId: string, articleId: string, page: number) => void;
  onOpenPodcast: (episode: PodcastTopicResult) => void;
}): React.JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TopicSearchResult[]>([]);
  const [scope, setScope] = useState<TopicSearchScope>("all");
  const [searchedQuery, setSearchedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      dialog.showModal();
      inputRef.current?.focus();
      document.body.classList.add("search-open");
    } else {
      dialog.close();
    }
    return () => document.body.classList.remove("search-open");
  }, [open]);
  useEffect(() => () => requestRef.current?.abort(), []);
  const articleCount = results.filter((result) => result.content_type === "article").length;
  const podcastCount = results.length - articleCount;
  const visibleResults = scope === "all"
    ? results
    : results.filter((result) => result.content_type === scope);

  async function runSearch(nextQuery: string): Promise<void> {
    const normalized = nextQuery.trim();
    if (normalized.length < 2) return;
    trackFeature("search_submit");
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    setQuery(normalized);
    setLoading(true);
    setError("");
    try {
      const payload = await api<{
        query: string;
        count: number;
        results: TopicSearchResult[];
      }>(`/api/topics?q=${encodeURIComponent(normalized)}`, { signal: request.signal });
      if (request.signal.aborted) return;
      setSearchedQuery(payload.query);
      setResults(payload.results);
      setScope("all");
    } catch (reason) {
      if (request.signal.aborted) return;
      setError(reason instanceof Error ? reason.message : "主题检索失败");
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await runSearch(query);
  }

  return (
    <dialog
      ref={dialogRef}
      className="search-overlay"
      aria-label="全库主题检索"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <section className="search-sheet topic-search-sheet">
        <header className="topic-search-head">
          <button className="close-button" onClick={onClose} aria-label="关闭">×</button>
          <p className="eyebrow">CROSS-LIBRARY RESEARCH</p>
          <h2>全库检索</h2>
          <p className="search-intro">从期刊正文、文章语义和播客逐字稿中，找到真正围绕同一问题展开的内容。</p>
          <form className="topic-search-form" onSubmit={(event) => void submit(event)}>
            <input
              ref={inputRef}
              aria-label="搜索行业、公司、人物或研究主题"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="输入行业、公司、人物或研究主题"
              minLength={2}
              required
            />
            <button disabled={loading} type="submit">
              {loading ? "检索中…" : "检索"}
            </button>
          </form>
          <div className="topic-suggestions" aria-label="常用主题">
            <span>常用主题</span>
            {["人工智能", "生物医药", "商业航天", "投资策略"].map((topic) => (
              <button key={topic} onClick={() => void runSearch(topic)}>{topic}</button>
            ))}
          </div>
        </header>
        <div className="topic-search-body" aria-busy={loading}>
          {opening && <p className="search-status" role="status">正在打开内容…</p>}
          {openError && <p className="error-banner" role="alert">{openError}</p>}
          {loading && <p className="search-status" role="status">正在查找“{query}”相关内容…</p>}
          {error && <p className="error-banner" role="alert">{error}</p>}
          {searchedQuery && (
            <div className="topic-results-toolbar">
              <p><b>“{searchedQuery}”</b> · 按相关性排序</p>
              <span>共 {results.length} 条</span>
            </div>
          )}
          {searchedQuery && results.length > 0 && (
            <nav className="topic-result-scopes" aria-label="筛选内容类型">
              {([
                ["all", "全部", results.length],
                ["article", "期刊", articleCount],
                ["podcast", "播客", podcastCount],
              ] as const).map(([value, label, count]) => (
                <button
                  className={scope === value ? "active" : ""}
                  aria-pressed={scope === value}
                  key={value}
                  onClick={() => setScope(value)}
                >
                  {label}<small>{count}</small>
                </button>
              ))}
            </nav>
          )}
          {searchedQuery && visibleResults.length === 0 && !loading && !error && (
            <div className="topic-empty">
              <h3>还没有找到足够相关的内容</h3>
              <p>可以缩短主题词，或换成行业、机构、政策名称再试一次。</p>
            </div>
          )}
          {visibleResults.length > 0 && (
            <div className="topic-result-list">
              {visibleResults.map((result) => {
                const isArticle = result.content_type === "article";
                const key = isArticle ? result.global_id : result.id;
                const source = isArticle ? result.publication : result.feed_title;
                const summary = result.summary || (!isArticle ? result.description : "");
                const tags = isArticle ? result.tags : result.topics;
                return (
                  <button
                    className="topic-result-row"
                    key={key}
                    onClick={() => {
                      if (isArticle) {
                        onOpen(result.issue_id, result.article_id, result.page_start);
                      } else {
                        onOpenPodcast(result);
                      }
                    }}
                  >
                    <span className="topic-result-kind">
                      {isArticle ? "期刊文章" : "长播客"} · {source}
                    </span>
                    <b className="topic-result-title">{result.title}</b>
                    <p>{summary}</p>
                    <small className="topic-result-tags">{tags.slice(0, 4).join(" · ")}</small>
                    <span className="topic-result-evidence">
                      <strong>{result.match_reason}</strong>
                      <small>
                        {isArticle
                          ? `${result.issue_label} · P.${result.page_start}–${result.page_end}`
                          : result.transcriptUrl ? "可阅读全文与逐字稿" : "可阅读精华解读"}
                      </small>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {searchedQuery && results.length > 0 && (
            <details className="search-method">
              <summary>检索说明</summary>
              <p>优先考虑标题、标签和摘要中的直接命中，再结合全文关键词、主题同义词与语义相关性；逐字稿中的偶然提及不会再排在主题内容之前。</p>
            </details>
          )}
        </div>
      </section>
    </dialog>
  );
}

type PodcastReaderTurn = {
  id: string;
  chapterId: string;
  speaker: string;
  sourceSpeaker: string;
  text: string;
  sourceText: string;
};

type PodcastReaderMode = "digest" | "transcript";

const CHINESE_TRANSCRIPT_PENDING = "中文逐字稿生成中，完成后将自动开放阅读。";

function hasChineseTranscript(episode: PodcastEpisode): boolean {
  return Boolean(episode.transcriptUrl && episode.transcriptReady);
}

function isChineseLanguage(language: string): boolean {
  return /^zh(?:[-_]|$)/iu.test(language);
}

function PodcastReader({
  episode,
  onBack,
}: {
  episode: PodcastEpisode;
  onBack: () => void;
}): React.JSX.Element {
  const [transcript, setTranscript] = useState<TranscriptDocument | null>(null);
  const [article, setArticle] = useState<ArticleDocument | null>(null);
  const [mode, setMode] = useState<PodcastReaderMode>(
    episode.articleUrl ? "digest" : "transcript",
  );
  const [showSource, setShowSource] = useState(false);
  const [activeChapter, setActiveChapter] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const [transcriptError, setTranscriptError] = useState("");
  const [articleError, setArticleError] = useState("");
  const chapterNodes = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    let cancelled = false;
    setTranscript(null);
    setTranscriptError("");
    if (!episode.transcriptUrl) {
      setTranscriptError("中文逐字稿尚未入库");
      return () => {
        cancelled = true;
      };
    }
    if (!hasChineseTranscript(episode)) {
      setTranscriptError(CHINESE_TRANSCRIPT_PENDING);
      return () => {
        cancelled = true;
      };
    }
    void fetch(episode.transcriptUrl, {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("中文逐字稿读取失败");
        const payload: unknown = await response.json();
        if (!isTranscriptDocument(payload)) throw new Error("中文逐字稿格式无效");
        if (!isChineseLanguage(payload.language)) {
          throw new Error(CHINESE_TRANSCRIPT_PENDING);
        }
        return payload;
      })
      .then((payload) => {
        if (!cancelled) setTranscript(payload);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setTranscriptError(reason instanceof Error ? reason.message : "中文逐字稿读取失败");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [episode.transcriptReady, episode.transcriptUrl]);

  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setArticleError("");
    setMode(episode.articleUrl ? "digest" : "transcript");
    setActiveChapter("");
    if (!episode.articleUrl) {
      return () => {
        cancelled = true;
      };
    }
    void fetch(episode.articleUrl, {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("精华解读读取失败");
        const payload: unknown = await response.json();
        if (!isArticleDocument(payload)) throw new Error("精华解读格式无效");
        return payload;
      })
      .then((payload) => {
        if (!cancelled) setArticle(payload);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setArticleError(reason instanceof Error ? reason.message : "精华解读读取失败");
          setMode("transcript");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [episode.articleUrl]);

  const chapters: TranscriptChapter[] = transcript?.chapters?.length
    ? transcript.chapters.map((chapter, index) => ({
        ...chapter,
        title: podcastSectionTitle(chapter.title, index + 1),
      }))
    : (transcript?.segments.map((segment, index) => ({
        id: `C${String(index + 1).padStart(4, "0")}`,
        source_title: formatPodcastTime(segment.start_seconds),
        title: `第 ${index + 1} 节`,
        start_seconds: segment.start_seconds,
        end_seconds: segment.end_seconds,
      })) ?? []);
  const articleChapters: TranscriptChapter[] = article?.sections.map((section, index) => ({
    id: `A${String(index + 1).padStart(4, "0")}`,
    source_title: section.title,
    title: podcastSectionTitle(section.title, index + 1),
    start_seconds: section.sourceStartSeconds,
    end_seconds: section.sourceStartSeconds + 1,
  })) ?? [];
  const navigation = mode === "digest" ? articleChapters : chapters;

  function chapterAt(seconds: number): string {
    let chapterId = chapters[0]?.id ?? "";
    for (const chapter of chapters) {
      if (chapter.start_seconds > seconds) break;
      chapterId = chapter.id;
    }
    return chapterId;
  }

  const turns: PodcastReaderTurn[] = transcript?.segments.flatMap((segment) => {
    if (segment.turns?.length) {
      return segment.turns.map((turn) => ({
        id: turn.id,
        chapterId: turn.chapter_id ?? chapterAt(turn.start_seconds ?? segment.start_seconds),
        speaker: turn.speaker,
        sourceSpeaker: turn.source_speaker,
        text: turn.zh_text,
        sourceText: turn.source_text,
      }));
    }
    return transcriptTurns(segment).map((turn, index) => ({
      id: `${segment.id}-T${String(index + 1).padStart(4, "0")}`,
      chapterId: chapterAt(segment.start_seconds),
      ...turn,
    }));
  }) ?? [];

  useEffect(() => {
    if (!navigation.length) return;
    setActiveChapter(navigation[0].id);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top);
        const chapterId = visible[0]?.target.id;
        if (chapterId) setActiveChapter(chapterId);
      },
      { rootMargin: "-18% 0px -68%", threshold: 0 },
    );
    for (const chapter of navigation) {
      const node = chapterNodes.current[chapter.id];
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [article, mode, transcript]);

  function jumpToChapter(chapterId: string): void {
    setActiveChapter(chapterId);
    chapterNodes.current[chapterId]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTocOpen(false);
  }

  return (
    <main className="podcast-room podcast-verbatim-room" onKeyDown={(event) => { if (event.key === "Escape") setTocOpen(false); }}>
      {tocOpen && (
        <button
          className="podcast-toc-scrim"
          aria-label="关闭播客目录"
          onClick={() => setTocOpen(false)}
        />
      )}
      <aside className={`podcast-sidebar podcast-chapter-sidebar${tocOpen ? " is-open" : ""}`}>
        <header>
          <button className="back-button" onClick={onBack}>← 返回阅览室</button>
          <p className="eyebrow">{episode.feed_title}</p>
          <h1>{episode.title}</h1>
          <span>{navigation.length} 个章节 · {mode === "digest" ? "中文精华解读" : "中文逐字稿"}</span>
        </header>
        <nav aria-label="播客章节">
          {navigation.map((chapter, index) => (
            <button
              className={activeChapter === chapter.id ? "active" : ""}
              aria-current={activeChapter === chapter.id ? "location" : undefined}
              key={chapter.id}
              data-analytics-feature="podcast_chapter" onClick={() => jumpToChapter(chapter.id)}
            >
              <small>
                {String(index + 1).padStart(2, "0")}
                {mode === "transcript" && transcript?.timing !== "estimated"
                  ? ` · ${formatPodcastTime(chapter.start_seconds)}`
                  : ""}
              </small>
              <b>{chapter.title}</b>
            </button>
          ))}
        </nav>
        <footer className="podcast-source-links">
          <a href={episode.source_url} target="_blank" rel="noreferrer">打开原节目 ↗</a>
        </footer>
      </aside>
      <section className="podcast-content podcast-verbatim-content">
        <header className="podcast-verbatim-header">
          <button className="back-button podcast-mobile-back" onClick={onBack}>← 返回阅览室</button>
          <button className="podcast-toc-trigger" data-analytics-feature="toc_open" onClick={() => setTocOpen(true)}>章节目录</button>
          <p className="eyebrow">
            {mode === "digest" ? "EDITORIAL DIGEST" : "FULL TRANSCRIPT"}
          </p>
          <h1>{episode.title}</h1>
          <p>{mode === "digest" ? episode.summary : "按原节目顺序提供完整中文翻译，并保留原文对照。"}</p>
          <nav className="podcast-reading-modes" aria-label="播客阅读方式">
            {episode.articleUrl && (
              <button className={mode === "digest" ? "active" : ""} data-analytics-feature="podcast_mode" onClick={() => setMode("digest")}>精华解读</button>
            )}
            <button className={mode === "transcript" ? "active" : ""} data-analytics-feature="podcast_mode" onClick={() => setMode("transcript")}>中文逐字稿</button>
          </nav>
          {mode === "transcript" && transcript?.sourceLanguage !== transcript?.language && (
            <button
              className={showSource ? "source-toggle active" : "source-toggle"}
              aria-pressed={showSource}
              data-analytics-feature="podcast_mode" onClick={() => setShowSource((value) => !value)}
            >
              {showSource ? "隐藏原文" : "对照原文"}
            </button>
          )}
        </header>
        <PodcastPlayer
          episodeId={episode.id}
          audioUrl={episode.sourceAudioUrl ?? episode.audioUrl}
          youtubeUrl={episode.source_url}
          title={episode.title}
        />
        {mode === "digest" && articleError && <p className="error-banner">{articleError}</p>}
        {mode === "transcript" && transcriptError && (
          <p className={transcriptError === CHINESE_TRANSCRIPT_PENDING
            ? "podcast-transcript-pending"
            : "error-banner"}
          >
            {transcriptError}
          </p>
        )}
        {mode === "digest" && !article && !articleError && <div className="loader">正在装载精华解读…</div>}
        {mode === "transcript" && !transcript && !transcriptError && <div className="loader">正在装载中文逐字稿…</div>}
        {mode === "digest" && article && (
          <article className="podcast-verbatim podcast-digest">
            <section className="podcast-takeaways">
              <p className="eyebrow">核心看点</p>
              <ul>{article.takeaways.map((takeaway, index) => <li key={`${index}-${takeaway}`}>{takeaway}</li>)}</ul>
            </section>
            {article.sections.map((section, sectionIndex) => {
              const sectionId = `A${String(sectionIndex + 1).padStart(4, "0")}`;
              return (
                <section
                  className="podcast-chapter podcast-digest-section"
                  id={sectionId}
                  key={sectionId}
                  ref={(node) => {
                    chapterNodes.current[sectionId] = node;
                  }}
                >
                  <header>
                    <span>{String(sectionIndex + 1).padStart(2, "0")}</span>
                    <div><h2>{podcastSectionTitle(section.title, sectionIndex + 1)}</h2></div>
                  </header>
                  <div className="podcast-digest-copy">
                    {section.paragraphs.map((paragraph, index) => <p key={`${index}-${paragraph}`}>{paragraph}</p>)}
                  </div>
                </section>
              );
            })}
            <footer className="podcast-digest-conclusion">
              <p className="eyebrow">结语</p>
              <p>{article.conclusion}</p>
            </footer>
          </article>
        )}
        {mode === "transcript" && transcript && (
          <article className="podcast-verbatim">
            {chapters.map((chapter, chapterIndex) => {
              const chapterTurns = turns.filter((turn) => turn.chapterId === chapter.id);
              return (
                <section
                  className="podcast-chapter"
                  id={chapter.id}
                  key={chapter.id}
                  ref={(node) => {
                    chapterNodes.current[chapter.id] = node;
                  }}
                >
                  <header>
                    <span>{String(chapterIndex + 1).padStart(2, "0")}</span>
                    <div>
                      <small>{formatPodcastTime(chapter.start_seconds)}</small>
                      <h2>{chapter.title}</h2>
                    </div>
                  </header>
                  <div className="podcast-turns">
                    {chapterTurns.map((turn) => (
                      <section className="podcast-turn" key={turn.id}>
                        <div>
                          <strong>{turn.speaker}</strong>
                          {showSource && <small>{turn.sourceSpeaker}</small>}
                        </div>
                        <div>
                          <p>{turn.text}</p>
                          {showSource && <p className="transcript-source">{turn.sourceText}</p>}
                        </div>
                      </section>
                    ))}
                  </div>
                </section>
              );
            })}
          </article>
        )}
      </section>
    </main>
  );
}

function App(): React.JSX.Element {
  const initialPodcastParams = new URLSearchParams(window.location.search);
  const initialPodcastSlug = podcastDeepLink(
    window.location.pathname,
    window.location.search,
  );
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [podcastTotal, setPodcastTotal] = useState(0);
  const [podcastNextOffset, setPodcastNextOffset] = useState<number | null>(null);
  const [podcastQuery, setPodcastQuery] = useState(
    initialPodcastParams.get("q") ?? "",
  );
  const [podcastFeed, setPodcastFeed] = useState(
    initialPodcastParams.get("feed") ?? "",
  );
  const [podcastTopic, setPodcastTopic] = useState(
    initialPodcastParams.get("topic") ?? "",
  );
  const [podcastFacets, setPodcastFacets] = useState<PodcastFacets>({
    feeds: [],
    topics: [],
  });
  const [podcastLoadingMore, setPodcastLoadingMore] = useState(false);
  const [home, setHome] = useState<HomePayload | null>(null);
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [episodesLoading, setEpisodesLoading] = useState(true);
  const [active, setActive] = useState<Issue | null>(null);
  const [activePodcast, setActivePodcast] = useState<PodcastEpisode | null>(null);
  const [aboutOpen, setAboutOpen] = useState(
    initialPodcastParams.get("view") === "about",
  );
  const [analyticsOpen, setAnalyticsOpen] = useState(window.location.pathname === "/admin/analytics" || initialPodcastParams.get("view") === "analytics");
  const [librarySection, setLibrarySection] = useState<LibrarySection>(
    initialPodcastSlug ? "podcasts" : librarySectionFromSearch(window.location.search),
  );
  const [view, setView] = useState<View>("text");
  const [page, setPage] = useState(1);
  const [initialReadingPage, setInitialReadingPage] = useState(1);
  const [readingJumpRequest, setReadingJumpRequest] = useState(0);
  const [targetArticleId, setTargetArticleId] = useState<string | null>(null);
  const [activeArticleContext, setActiveArticleContext] =
    useState<ArticleContext | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [publicationFilter, setPublicationFilter] = useState<PublicationFilter>("all");
  const [immersive, setImmersive] = useState(false);
  const [error, setError] = useState("");
  const readingRoomRef = useRef<HTMLElement | null>(null);
  const navigationRequest = useRef(0);
  const podcastRequest = useRef<AbortController | null>(null);
  const loadedPodcastFilter = useRef<{ query: string; feed: string; topic: string } | null>(null);
  const [opening, setOpening] = useState(false);
  const [homeError, setHomeError] = useState("");

  useEffect(() => {
    if (!user) return;
    return startAnalytics(user.id);
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    if (opening || (!analyticsOpen && !aboutOpen && !active && !activePodcast &&
        (periodicalDeepLink(window.location.search) || podcastDeepLink(window.location.pathname, window.location.search)))) {
      pauseAnalyticsReading();
      return;
    }
    if (analyticsOpen) setAnalyticsContext({ screen: "admin" });
    else if (aboutOpen) setAnalyticsContext({ screen: "about" });
    else if (activePodcast) setAnalyticsContext({ screen: "podcast", contentType: "podcast", contentId: activePodcast.id, reading: !searchOpen });
    else if (active) setAnalyticsContext({ screen: "reader",
      contentType: activeArticleContext ? "article" : "issue",
      contentId: activeArticleContext ? `${active.id}:${activeArticleContext.id}` : active.id,
      reading: view === "text" && !searchOpen,
    });
    else setAnalyticsContext({ screen: librarySection });
  }, [user?.id, analyticsOpen, aboutOpen, activePodcast?.id, active?.id, activeArticleContext?.id, librarySection, view, searchOpen, opening]);

  useEffect(() => { if (user && searchOpen) trackFeature("search_open"); }, [user?.id, searchOpen]);

  const saveReadingProgress = useCallback((progress: ReadingProgress): void => {
    if (!active) return;
    setIssues((current) => current.map((issue) => issue.id === active.id
      ? { ...issue, progress_page: progress.pageNumber, progress_ratio: progress.progress }
      : issue));
    setHome((current) => current ? {
      ...current,
      continue_reading: [{
        type: "issue" as const, id: active.id, title: active.title,
        publication: active.publication, issue_label: active.issue_label,
        issue_date: active.issue_date, page_count: active.page_count,
        page_number: progress.pageNumber, progress: progress.progress,
        cover_url: active.coverUrl ?? null, updated_at: new Date().toISOString(),
      }, ...current.continue_reading.filter((item) => item.id !== active.id)].slice(0, 4),
    } : current);
    void api(`/api/issues/${encodeURIComponent(active.id)}/progress`, {
      method: "PUT", body: JSON.stringify(progress), keepalive: true,
    }).catch(() => undefined);
  }, [active]);

  function loadHome(): void {
    setHomeError("");
    void api<HomePayload>("/api/home").then(setHome).catch(() => setHomeError("精选暂时未能加载，请重试或先浏览期刊与播客。"));
  }

  const trackEngagement = useCallback((event: EngagementEvent): void => {
    void api("/api/events", {
      method: "POST",
      body: JSON.stringify(event),
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    void api<{ user: User }>("/api/me")
      .then((me) => {
        setUser(me.user);
        void api<{ issues: Issue[] }>("/api/issues")
          .then((library) => {
            setIssues(library.issues);
            const deepLink = periodicalDeepLink(window.location.search);
            const linkedIssue = deepLink
              ? library.issues.find((issue) => issue.id === deepLink.issueId)
              : null;
            if (deepLink && linkedIssue) {
              void openIssue(linkedIssue, null, readingArticleFromLocation(window.location.search, window.location.hash), false);
            }
          })
          .catch((reason: unknown) => {
            setError(reason instanceof Error ? reason.message : "期刊书架加载失败");
          })
          .finally(() => setIssuesLoading(false));
        void loadPodcastPage(podcastQuery, 0, false, podcastFeed, podcastTopic);
        const linkedPodcastSlug = podcastDeepLink(
          window.location.pathname,
          window.location.search,
        );
        if (linkedPodcastSlug) {
          void openPodcastBySlug(linkedPodcastSlug);
        }
        loadHome();
        trackEngagement({
          eventType: "home_view",
          contentType: "home",
          contentId: "home",
          source: "home",
        });
      })
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [trackEngagement]);

  useEffect(() => {
    function handleFullscreenChange(): void {
      setImmersive(document.fullscreenElement === readingRoomRef.current);
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    function handlePopState(): void {
      navigationRequest.current += 1;
      setOpening(false);
      setError("");
      setSearchOpen(false);
      setView("text");
      setImmersive(false);
      const params = new URLSearchParams(window.location.search);
      setAboutOpen(params.get("view") === "about");
      setAnalyticsOpen(window.location.pathname === "/admin/analytics" || params.get("view") === "analytics");
      setLibrarySection(libraryLocationFromUrl(window.location.pathname, window.location.search));
      setActive(null);
      setActivePodcast(null);
      const linkedIssue = periodicalDeepLink(window.location.search);
      if (linkedIssue) {
        void openIssue({ id: linkedIssue.issueId }, null, readingArticleFromLocation(window.location.search, window.location.hash), false);
      }
      const query = params.get("q") ?? "";
      const feed = params.get("feed") ?? "";
      const topic = params.get("topic") ?? "";
      if (params.get("mode") === "podcasts") {
        setPodcastQuery(query);
        setPodcastFeed(feed);
        setPodcastTopic(topic);
        const loaded = loadedPodcastFilter.current;
        if (loaded?.query === query.trim() && loaded.feed === feed && loaded.topic === topic) {
          podcastRequest.current?.abort();
          setEpisodesLoading(false);
          setPodcastLoadingMore(false);
        } else {
          void loadPodcastPage(query, 0, false, feed, topic);
        }
      }
      const linkedPodcastSlug = podcastDeepLink(
        window.location.pathname,
        window.location.search,
      );
      if (linkedPodcastSlug) {
        setLibrarySection("podcasts");
        void openPodcastBySlug(linkedPodcastSlug);
      }
      const scrollY = Number(window.history.state?.folioScrollY) || 0;
      window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "instant" }));
    }
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") {
        setSearchOpen(false);
        setView((current) => isReaderDrawerView(current) ? "text" : current);
      }
    }
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const open = isReaderDrawerView(view);
    document.body.classList.toggle("reader-drawer-open", open);
    return () => document.body.classList.remove("reader-drawer-open");
  }, [view]);

  async function openIssue(
    issue: Pick<Issue, "id">,
    targetPage?: number | null,
    articleId?: string | null,
    pushHistory = true,
  ): Promise<void> {
    const request = ++navigationRequest.current;
    setOpening(true);
    setError("");
    try {
      const payload = await api<{ issue: Issue }>(`/api/issues/${encodeURIComponent(issue.id)}`);
      if (request !== navigationRequest.current) return;
      if (pushHistory) {
        window.history.replaceState({ ...window.history.state, folioScrollY: window.scrollY }, "");
        window.history.pushState({ folioReader: true }, "", issueReaderUrl(issue.id, articleId, window.location.search));
      }
      setAboutOpen(false);
      setActive(payload.issue);
      setActivePodcast(null);
      setPage(targetPage ?? payload.issue.progress_page ?? 1);
      setInitialReadingPage(targetPage ?? payload.issue.progress_page ?? 1);
      setTargetArticleId(articleId ?? null);
      setActiveArticleContext(null);
      setView("text");
      setSearchOpen(false);
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }));
    } catch (reason) {
      if (request !== navigationRequest.current) return;
      setError(reason instanceof Error ? reason.message : "读取失败");
    } finally {
      if (request === navigationRequest.current) setOpening(false);
    }
  }

  function openHomeArticle(
    article: HomeArticle,
    source: "home" | "library",
  ): void {
    const issue = issues.find((item) => item.id === article.issue_id);
    if (!issue) {
      setError("文章所属期刊尚未完成上架");
      return;
    }
    trackEngagement({
      eventType: "article_open",
      contentType: "article",
      contentId: article.global_id,
      issueId: article.issue_id,
      source,
    });
    void openIssue(issue, article.page_start, article.article_id);
  }

  function continueReading(item: ContinueReadingItem): void {
    const issue = issues.find((candidate) => candidate.id === item.id);
    if (!issue) return;
    trackEngagement({
      eventType: "issue_open",
      contentType: "issue",
      contentId: issue.id,
      issueId: issue.id,
      source: "home",
      progress: item.progress,
    });
    void openIssue(issue, item.page_number);
  }

  function openHomePodcast(item: HomePodcast): void {
    void openPodcast(item);
  }

  function openLibraryIssue(issue: Issue): void {
    trackEngagement({
      eventType: "issue_open",
      contentType: "issue",
      contentId: issue.id,
      issueId: issue.id,
      source: "library",
    });
    void openIssue(issue);
  }

  async function openPodcast(episode: { id: string }): Promise<void> {
    const request = ++navigationRequest.current;
    setOpening(true);
    setError("");
    try {
      const payload = await api<{ episode: PodcastEpisode }>(
        `/api/podcasts/${encodeURIComponent(episode.id)}`,
      );
      if (request !== navigationRequest.current) return;
      setAboutOpen(false);
      setActive(null);
      setActivePodcast(payload.episode);
      setSearchOpen(false);
      window.history.replaceState({ ...window.history.state, folioScrollY: window.scrollY }, "");
      window.history.pushState(
        { folioReader: true },
        "",
        podcastCanonicalPath(payload.episode.publicSlug),
      );
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }));
    } catch (reason) {
      if (request !== navigationRequest.current) return;
      setError(reason instanceof Error ? reason.message : "播客读取失败");
    } finally {
      if (request === navigationRequest.current) setOpening(false);
    }
  }

  async function openPodcastBySlug(publicSlug: string): Promise<void> {
    const request = ++navigationRequest.current;
    setOpening(true);
    setError("");
    try {
      const payload = await api<{ episode: PodcastEpisode }>(
        `/api/podcasts-by-slug/${encodeURIComponent(publicSlug)}`,
      );
      if (request !== navigationRequest.current) return;
      setActive(null);
      setActivePodcast(payload.episode);
      setSearchOpen(false);
      window.history.replaceState(
        window.history.state,
        "",
        podcastCanonicalPath(payload.episode.publicSlug),
      );
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }));
    } catch (reason) {
      if (request !== navigationRequest.current) return;
      setError(reason instanceof Error ? reason.message : "播客读取失败");
    } finally {
      if (request === navigationRequest.current) setOpening(false);
    }
  }

  async function loadPodcastPage(
    query: string,
    offset: number,
    append: boolean,
    feed = podcastFeed,
    topic = podcastTopic,
  ): Promise<void> {
    podcastRequest.current?.abort();
    const request = new AbortController();
    podcastRequest.current = request;
    const params = new URLSearchParams({ limit: "60", offset: String(offset) });
    if (query.trim()) params.set("q", query.trim());
    if (feed) params.set("feed", feed);
    if (topic) params.set("topic", topic);
    setPodcastLoadingMore(append);
    setEpisodesLoading(!append);
    setError("");
    try {
      const payload = await api<PodcastLibraryPayload>(`/api/podcasts?${params.toString()}`, { signal: request.signal });
      if (request.signal.aborted) return;
      loadedPodcastFilter.current = { query: query.trim(), feed, topic };
      setEpisodes((current) => append ? [...current, ...payload.episodes] : payload.episodes);
      setPodcastTotal(payload.total);
      setPodcastNextOffset(payload.nextOffset);
      setPodcastFacets(payload.facets);
    } catch (reason) {
      if (request.signal.aborted) return;
      setError(reason instanceof Error ? reason.message : "播客书架加载失败");
    } finally {
      if (!request.signal.aborted) {
        setPodcastLoadingMore(false);
        setEpisodesLoading(false);
      }
    }
  }

  function searchPodcasts(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    updatePodcastUrl(podcastQuery, podcastFeed, podcastTopic);
    void loadPodcastPage(podcastQuery, 0, false);
  }

  function updatePodcastUrl(query: string, feed: string, topic: string): void {
    const params = new URLSearchParams();
    params.set("mode", "podcasts");
    if (query.trim()) params.set("q", query.trim());
    if (feed) params.set("feed", feed);
    if (topic) params.set("topic", topic);
    window.history.pushState(null, "", `/?${params.toString()}`);
  }

  function filterPodcasts(feed: string, topic: string): void {
    setPodcastFeed(feed);
    setPodcastTopic(topic);
    updatePodcastUrl(podcastQuery, feed, topic);
    void loadPodcastPage(podcastQuery, 0, false, feed, topic);
  }

  function clearPodcastFilters(): void {
    setPodcastQuery("");
    setPodcastFeed("");
    setPodcastTopic("");
    updatePodcastUrl("", "", "");
    void loadPodcastPage("", 0, false, "", "");
  }

  function libraryUrl(section: LibrarySection): string {
    if (section === "podcasts") {
      const params = new URLSearchParams({ mode: "podcasts" });
      if (podcastQuery.trim()) params.set("q", podcastQuery.trim());
      if (podcastFeed) params.set("feed", podcastFeed);
      if (podcastTopic) params.set("topic", podcastTopic);
      return `/?${params.toString()}`;
    }
    if (section === "periodicals") {
      return "/?mode=periodicals";
    }
    return "/";
  }

  function showLibrarySection(section: LibrarySection): void {
    setAnalyticsOpen(false);
    navigationRequest.current += 1;
    setOpening(false);
    setError("");
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => setImmersive(false));
    }
    setImmersive(false);
    setLibrarySection(section);
    setAboutOpen(false);
    setActive(null);
    setActivePodcast(null);
    setSearchOpen(false);
    setTargetArticleId(null);
    setActiveArticleContext(null);
    window.history.pushState(null, "", libraryUrl(section));
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  function openHomeIssue(item: HomeIssue): void {
    const issue = issues.find((candidate) => candidate.id === item.id);
    if (issue) openLibraryIssue(issue);
  }

  function updateLibraryUrl(section: LibrarySection): void {
    setLibrarySection(section);
    if (section === "podcasts") {
      updatePodcastUrl(podcastQuery, podcastFeed, podcastTopic);
      return;
    }
    window.history.replaceState(null, "", libraryUrl(section));
  }

  function openAbout(): void {
    setAnalyticsOpen(false);
    navigationRequest.current += 1;
    setOpening(false);
    setError("");
    setAboutOpen(true);
    setActive(null);
    setActivePodcast(null);
    setSearchOpen(false);
    const params = new URLSearchParams(new URL(libraryUrl(librarySection), window.location.origin).search);
    params.set("view", "about");
    window.history.pushState(null, "", `/?${params.toString()}`);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }));
  }

  function returnToLibrary(): void {
    setAnalyticsOpen(false);
    navigationRequest.current += 1;
    setOpening(false);
    if (window.history.state?.folioReader) {
      window.history.back();
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => setImmersive(false));
    }
    setImmersive(false);
    setActive(null);
    setActivePodcast(null);
    setAboutOpen(false);
    setTargetArticleId(null);
    setActiveArticleContext(null);
    updateLibraryUrl(librarySection);
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  function toggleImmersive(): void {
    const room = readingRoomRef.current;
    if (!room) return;
    const command = immersiveCommand(
      immersive,
      document.fullscreenElement === room,
    );
    if (command === "exit-fullscreen") {
      void document.exitFullscreen().catch(() => setImmersive(false));
      return;
    }
    if (command === "exit-fallback") {
      setImmersive(false);
      return;
    }
    setImmersive(true);
    void room.requestFullscreen().catch(() => {
      setImmersive(true);
    });
  }

  async function logout(): Promise<void> {
    await api("/auth/logout", { method: "POST" });
    window.location.reload();
  }

  if (checking) return <BootScreen />;
  if (!user) return <Login />;
  const articleTotal = issues.reduce(
    (total, issue) => total + (issue.article_count ?? 0),
    0,
  );
  const newestIds = newestIssueIds(issues);
  const availableThemes = [
    ...new Map(
      issues.map((issue) => {
        const theme = publicationTheme(issue.publication);
        return [theme.key, theme] as const;
      }),
    ).values(),
  ];
  const visibleIssues = publicationFilter === "all"
    ? issues
    : issues.filter(
      (issue) => publicationTheme(issue.publication).key === publicationFilter,
    );
  const latestHomeIssues: HomeIssue[] = issues.slice(0, 4).map((issue) => ({
    id: issue.id,
    title: issue.title,
    publication: issue.publication,
    issue_label: issue.issue_label,
    cover_url: issue.coverUrl ?? null,
  }));

  return (
    <div className="app-shell">
      <header className="topbar">
        <button
          className="brand"
          onClick={() => showLibrarySection("home")}
          aria-label="返回 FOLIO 首页" data-analytics-feature="nav_home"
        >
          <img className="brand-seal" src="/brand-mark.svg" alt="" />
          <span className="brand-wordmark">
            <b>FOLIO</b>
            <small>回到原文，形成判断</small>
          </span>
        </button>
        <LibraryNavigation
          section={librarySection}
          onSelect={showLibrarySection}
        />
        <nav className="topbar-actions" aria-label="账户与工具">
          {!aboutOpen && (
            <button className="topbar-search" onClick={() => setSearchOpen(true)}>主题检索 <kbd>⌘ K</kbd></button>
          )}
          <button
            className={aboutOpen ? "topbar-about active" : "topbar-about"}
            aria-current={aboutOpen ? "page" : undefined}
            data-analytics-feature="about_open" onClick={openAbout}
          >
            关于
          </button>
          <span className="user-chip">
            {user.avatar_url ? <img src={user.avatar_url} alt="" /> : <i>{user.name.slice(0, 1)}</i>}
            {user.name}
          </span>
          <button className="quiet-button" data-analytics-feature="logout" onClick={() => void logout()}>退出</button>
        </nav>
      </header>

      {opening && <div className="navigation-status" role="status">正在打开内容…</div>}
      {error && <div className="app-error" role="alert"><p className="error-banner">{error}</p><button onClick={() => setError("")} aria-label="关闭错误提示">×</button></div>}
      {analyticsOpen ? (
        user.canViewAnalytics
          ? <AnalyticsDashboard onClose={() => showLibrarySection("home")} />
          : <main className="empty-state"><h1>此后台仅限开发者账号访问</h1><button onClick={() => showLibrarySection("home")}>返回首页</button></main>
      ) : aboutOpen ? (
        <AboutPage onBack={returnToLibrary} />
      ) : activePodcast ? (
        <PodcastReader
          episode={activePodcast}
          onBack={returnToLibrary}
        />
      ) : !active ? (
        <main className={`library library-${librarySection}`}>
          {librarySection === "home" ? (
            <section className="library-intro">
              <div className="library-intro-copy">
                <p className="eyebrow">YOUR READING ROOM</p>
                <h1>把注意力留给好内容。</h1>
                <p>
                  专业期刊与长播客，按主题发现，随时接着读。
                </p>
              </div>
              <aside className="library-index">
                <dl>
                  <div><dt>期刊</dt><dd>{issues.length}</dd></div>
                  <div><dt>播客</dt><dd>{podcastTotal}</dd></div>
                  <div><dt>文章</dt><dd>{articleTotal}</dd></div>
                </dl>
                <button className="library-search-link" onClick={() => setSearchOpen(true)}>
                  按主题查找全部内容
                  <span aria-hidden="true">→</span>
                </button>
              </aside>
            </section>
          ) : librarySection === "periodicals" ? (
            <section className="channel-intro">
              <div>
                <p className="eyebrow">PERIODICALS</p>
                <h1>期刊</h1>
              </div>
              <div className="channel-intro-copy">
                <p>{issues.length} 本期刊 · {articleTotal} 篇文章。按出版物浏览，或直接从主题进入正文。</p>
                <button className="channel-search-link" onClick={() => setSearchOpen(true)}>
                  按主题查找文章 →
                </button>
              </div>
            </section>
          ) : (
            <section className="channel-intro">
              <div>
                <p className="eyebrow">PODCASTS</p>
                <h1>播客</h1>
              </div>
              <div className="channel-intro-copy">
                <p>{podcastTotal} 期已收录。聚焦投资、科技与商业深度访谈，提供中文精华解读与逐字稿。</p>
              </div>
            </section>
          )}
          {librarySection === "home" ? (
            home ? (
              <HomeSections
                home={home}
                latestIssues={latestHomeIssues}
                onArticle={openHomeArticle}
                onContinue={continueReading}
                onIssue={openHomeIssue}
                onPodcast={openHomePodcast}
                onViewPeriodicals={() => showLibrarySection("periodicals")}
                onViewPodcasts={() => showLibrarySection("podcasts")}
              />
            ) : homeError ? (
              <div className="empty-library" role="alert">
                <h3>精选暂时无法加载</h3><p>{homeError}</p>
                <button className="primary-button" onClick={loadHome}>重新加载</button>
              </div>
            ) : (
              <div className="library-loading" aria-live="polite">
                <span />
                <p>正在整理今日精选…</p>
              </div>
            )
          ) : librarySection === "periodicals" ? (
            <>
              <section className="shelf-header">
                <div>
                  <h2>全部期刊</h2>
                  <span>{visibleIssues.length} 本期刊</span>
                </div>
                <nav className="publication-filters" aria-label="按期刊筛选">
                  <button
                    className={publicationFilter === "all" ? "active" : ""}
                    data-analytics-feature="publication_filter" onClick={() => setPublicationFilter("all")}
                  >
                    全部
                  </button>
                  {availableThemes.map((theme) => (
                    <button
                      className={publicationFilter === theme.key ? "active" : ""}
                      key={theme.key}
                      data-analytics-feature="publication_filter" onClick={() => setPublicationFilter(theme.key)}
                    >
                      {theme.label}
                    </button>
                  ))}
                </nav>
              </section>
              <section className="issue-grid">
                {visibleIssues.map((issue) => {
                  const theme = publicationTheme(issue.publication);
                  const style: PublicationStyle = {
                    "--publication-accent": theme.accent,
                    "--publication-surface": theme.surface,
                    "--publication-on-accent": theme.inkOnAccent,
                  };
                  return (
                    <button
                      className={`issue-card publication-${theme.key}`}
                      key={issue.id}
                      onClick={() => openLibraryIssue(issue)}
                      style={style}
                    >
                      <IssueCover issue={issue} isNewest={newestIds.has(issue.id)} />
                      <div className="issue-card-copy">
                        <p>{issue.publication}</p>
                        <h3>{issue.title}</h3>
                        <span>{issue.issue_label} · {issue.page_count} 页</span>
                        {issue.reading_status && issue.reading_status !== "ready" && (
                          <small className="issue-reading-status">{issue.language === "en" ? "中文翻译待完成" : "阅读版待整理"}</small>
                        )}
                        {issue.progress_ratio ? (
                          <div className="progress-track"><i style={{ width: `${issue.progress_ratio * 100}%` }} /></div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
                {issuesLoading && (
                  <div className="library-loading" aria-live="polite">
                    <span />
                    <p>正在整理期刊书架…</p>
                  </div>
                )}
                {!issuesLoading && issues.length === 0 && (
                  <div className="empty-library">
                    <span>01</span>
                    <h3>首期正在装帧</h3>
                    <p>期刊完成 OCR 与入库后会出现在这里。</p>
                  </div>
                )}
              </section>
            </>
          ) : (
            <>
              <section className="podcast-discovery" aria-label="筛选播客">
                <form className="podcast-search" onSubmit={searchPodcasts}>
                  <input
                    type="search"
                    value={podcastQuery}
                    onChange={(event) => setPodcastQuery(event.target.value)}
                    placeholder="搜索节目、嘉宾或主题"
                    aria-label="搜索播客"
                  />
                  <button type="submit">搜索</button>
                </form>
                <label className="podcast-feed-filter">
                  <span>节目</span>
                  <select
                    value={podcastFeed}
                    onChange={(event) => filterPodcasts(event.target.value, podcastTopic)}
                  >
                    <option value="">全部节目</option>
                    {podcastFacets.feeds.map((feed) => (
                      <option value={feed.id} key={feed.id}>
                        {feed.label}（{feed.total}）
                      </option>
                    ))}
                  </select>
                </label>
                {(podcastQuery || podcastFeed || podcastTopic) && (
                  <button className="podcast-clear-filters" onClick={clearPodcastFilters}>
                    清除筛选
                  </button>
                )}
              </section>
              <nav className="podcast-topic-filters" aria-label="按主题筛选">
                <button
                  className={podcastTopic === "" ? "active" : ""}
                  onClick={() => filterPodcasts(podcastFeed, "")}
                >
                  全部主题
                </button>
                {podcastFacets.topics.map((topic) => (
                  <button
                    className={podcastTopic === topic.id ? "active" : ""}
                    key={topic.id}
                    onClick={() => filterPodcasts(podcastFeed, topic.id)}
                  >
                    {topic.label}<small>{topic.total}</small>
                  </button>
                ))}
              </nav>
              <section className="podcast-grid">
                {episodes.map((episode) => {
                  const episodeMeta = [
                    formatPodcastPublishedAt(episode.published_at),
                    formatPodcastDuration(episode.duration_seconds),
                  ].filter(Boolean).join(" · ");

                  return (
                    <button
                      className="podcast-card"
                      key={episode.id}
                      onClick={() => void openPodcast(episode)}
                    >
                      <div className="podcast-artwork">
                        {episode.artwork_url ? (
                          <img src={episode.artwork_url} alt="" />
                        ) : (
                          <span>声</span>
                        )}
                      </div>
                      <div className="podcast-card-copy">
                        <p className="podcast-card-meta">
                          <strong>{episode.feed_title}</strong>
                          {episodeMeta ? <span>{episodeMeta}</span> : null}
                        </p>
                        <h3>{episode.title}</h3>
                        <div className="podcast-card-footer">
                          <span>
                            {episode.articleUrl ? "精华解读 · " : ""}
                            {hasChineseTranscript(episode) ? "中文逐字稿" : "中文逐字稿生成中"}
                          </span>
                          {episode.topics.length > 0 && (
                            <ul aria-label="主题标签">
                              {episode.topics.slice(0, 3).map((topic) => <li key={topic}>{topic}</li>)}
                            </ul>
                          )}
                        </div>
                        {episode.progress_ratio ? (
                          <div className="progress-track">
                            <i style={{ width: `${episode.progress_ratio * 100}%` }} />
                          </div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
                {episodesLoading && (
                  <div className="library-loading" aria-live="polite">
                    <span />
                    <p>正在整理播客书架…</p>
                  </div>
                )}
                {!episodesLoading && episodes.length === 0 && !error && (
                  <div className="empty-library">
                    <h3>{podcastQuery || podcastFeed || podcastTopic ? "没有找到匹配的播客" : "暂时还没有播客"}</h3>
                    <p>{podcastQuery || podcastFeed || podcastTopic ? "试试更短的关键词，或清除筛选浏览全部节目。" : "新节目准备好后会出现在这里。"}</p>
                    {(podcastQuery || podcastFeed || podcastTopic) && <button className="primary-button" onClick={clearPodcastFilters}>清除筛选</button>}
                  </div>
                )}
              </section>
              {podcastNextOffset !== null && (
                <button
                  className="podcast-load-more"
                  disabled={podcastLoadingMore || episodesLoading}
                  onClick={() => {
                    const filter = loadedPodcastFilter.current;
                    if (filter) void loadPodcastPage(filter.query, podcastNextOffset, true, filter.feed, filter.topic);
                  }}
                >
                  {podcastLoadingMore ? "正在加载…" : "加载更多"}
                </button>
              )}
            </>
          )}
          <section className="library-colophon" aria-label="内部阅读说明">
            <span>内部阅读说明</span>
            <p>
              内容仅供内部学习、研究与交流，不对外传播、不用于商业用途。
              期刊、播客、逐字稿及相关权利归原出版机构、节目和作者所有；
              中文精翻仅作阅读和理解辅助，引用请回到原始来源。
            </p>
          </section>
        </main>
      ) : (
        <main
          className={`reading-room${immersive ? " is-immersive" : ""}`}
          ref={readingRoomRef}
        >
          <header className="reading-context">
            <button
              className="back-button"
              onClick={returnToLibrary}
            >
              <span aria-hidden="true">←</span>
              返回书架
            </button>
            <div className="reading-context-issue">
              <span>{active.publication}</span>
              <b>{active.issue_label}</b>
            </div>
            <p className="reading-current-title">
              {activeArticleContext?.title ?? active.title}
            </p>
            <nav className="view-tabs" aria-label="阅读方式">
              {([
                ["text", "阅读"],
                ["comments", "讨论"],
                ["ai", "AI 研读"],
              ] as Array<[View, string]>).map(([key, label]) => (
                <button className={view === key ? "active" : ""} aria-pressed={view === key} data-analytics-feature={key === "comments" ? "discussion_open" : key === "ai" ? "ai_open" : undefined} key={key} onClick={() => {
                  setView(key);
                }}>{label}</button>
              ))}
            </nav>
          </header>
          <div className="reading-content">
              <TextReader
                issue={active}
                targetArticleId={targetArticleId}
                initialPage={initialReadingPage}
                jumpRequest={readingJumpRequest}
                onReadingProgress={saveReadingProgress}
                immersive={immersive}
                onToggleImmersive={toggleImmersive}
                onActiveArticle={setActiveArticleContext}
                onDiscuss={(article) => {
                  setActiveArticleContext(article);
                  setPage(article.pageStart);
                  setView("comments");
                }}
                onResearch={(article) => {
                  setActiveArticleContext(article);
                  setView("ai");
                }}
                onArticleEngagement={(eventType, article, progress, durationSeconds) => {
                  trackEngagement({
                    eventType,
                    contentType: "article",
                    contentId: `${active.id}:${article.id}`,
                    issueId: active.id,
                    progress,
                    durationSeconds,
                    source: "reader",
                  });
                }}
              />
          </div>
          {isReaderDrawerView(view) && (
            <div className="reader-drawer-layer">
              <button
                className="reader-drawer-scrim"
                aria-label="关闭阅读工具"
                onClick={() => setView("text")}
              />
              <aside
                className="reader-drawer"
                aria-label={view === "comments" ? "文章讨论" : "AI 研读"}
              >
                <button
                  className="reader-drawer-close"
                  aria-label="关闭阅读工具"
                  onClick={() => setView("text")}
                >
                  ×
                </button>
                {view === "comments" && (
                  <Comments
                    issue={active}
                    page={page}
                    article={activeArticleContext}
                  />
                )}
                {view === "ai" && (
                  <AiAssistant
                    issue={active}
                    article={activeArticleContext}
                    onJump={(target) => {
                      trackFeature("ai_citation");
                      setInitialReadingPage(target);
                      setTargetArticleId(null);
                      setReadingJumpRequest((value) => value + 1);
                      setView("text");
                    }}
                  />
                )}
              </aside>
            </div>
          )}
        </main>
      )}
      <TopicSearch
          open={searchOpen}
          opening={opening}
          openError={error}
          onClose={() => setSearchOpen(false)}
          onOpen={(issueId, articleId, targetPage) => {
            const issue = { id: issueId };
            trackEngagement({
              eventType: "article_open",
              contentType: "article",
              contentId: `${issueId}:${articleId}`,
              issueId,
              source: "search",
            });
            void openIssue(issue, targetPage, articleId);
          }}
          onOpenPodcast={(episode) => {
            void openPodcast(episode);
          }}
        />
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
